import { Info } from 'lucide-react';
import React, {
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { readCarePlanFromMetadata } from '@apps/web/components/outreaches/care_plan_summary_preview';
import { CreateOutreachForm } from '@apps/web/components/outreaches/create_outreach_form';
import {
    MULTI_SHIFT_OUTREACH_MAX,
    multiShiftOutreachMax,
} from '@apps/web/components/outreaches/edit_shifts_section';
import { HHAExchangeModifyReasonSelector } from '@apps/web/components/outreaches/hhaexchange_modify_reason_selector';
import { type ManualOutreachEntry } from '@apps/web/components/outreaches/manual_outreach_entry';
import { MissingCarePlanBanner } from '@apps/web/components/outreaches/missing_care_plan_banner';
import { MissingShiftWarningBanner } from '@apps/web/components/outreaches/missing_shift_warning_banner';
import {
    isShiftHeldByAnotherOutreach,
    MultiShiftCombobox,
} from '@apps/web/components/outreaches/multi_shift_combobox';
import { type OutreachDetailChrome } from '@apps/web/components/outreaches/outreach_detail_chrome';
import { buildEditFormSeed } from '@apps/web/components/outreaches/outreach_edit_form_seed';
import { QuickOutreachForm } from '@apps/web/components/outreaches/quick_outreach_form';
import {
    buildGroupsFromClients,
    sanitizeShiftGroups,
    type ShiftGroup,
    ShiftGroupEditor,
    type ShiftGroupMode,
} from '@apps/web/components/outreaches/shift_group_editor';
import { resolveAutomaticShiftGroupMode } from '@apps/web/components/outreaches/shift_grouping_defaults';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    type AutoAssignCaregiverPolicy,
    isAutoAssignAllowed,
    resolveClientAutoAssignDefault,
} from '@apps/web/lib/auto_assign';
import { getDataSourceDisplayName } from '@apps/web/lib/data_source_utils';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useMode } from '@apps/web/lib/mode_store';
import { type OutreachCreationAnalyticsContext } from '@apps/web/lib/outreach_creation_analytics';
import { orderShiftsForOutreach } from '@apps/web/lib/outreach_shift_ordering';
import {
    getAutoAssignShiftIds,
    getLabelForOutreachType,
    supportsOutreachAutoAssignFirstResponder,
} from '@apps/web/lib/outreach_utils';
import { isCancelledShift } from '@apps/web/lib/shift_cancellation';
import {
    useOrganization,
    useOrganizationClients,
    useOrgHHAExchangeModifyReasons,
    useOutreachTemplates,
    useShifts,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type JoinedOutreachRead,
    type Organization,
    type OrgClientRead,
    type OutreachTemplate,
    type Shift,
} from '@libraries/typescript/api_client';

function areShiftGroupsEqual(
    left: ReadonlyArray<ShiftGroup>,
    right: ReadonlyArray<ShiftGroup>,
): boolean {
    if (left.length !== right.length) {
        return false;
    }

    return left.every((leftGroup, index) => {
        const rightGroup = right[index];
        if (!rightGroup) {
            return false;
        }

        return (
            leftGroup.id === rightGroup.id &&
            leftGroup.groupMode === rightGroup.groupMode &&
            leftGroup.shiftIds.length === rightGroup.shiftIds.length &&
            leftGroup.shiftIds.every(
                (shiftId, shiftIndex) =>
                    shiftId === rightGroup.shiftIds[shiftIndex],
            )
        );
    });
}

function buildDefaultShiftGroups(
    selectedShifts: Array<Shift>,
    existingGroups: ReadonlyArray<ShiftGroup>,
    defaultShiftGroupMode: ShiftGroupMode,
) {
    const existingGroupsByShiftId = new Map<string, ShiftGroup>();
    for (const group of existingGroups) {
        for (const shiftId of group.shiftIds) {
            existingGroupsByShiftId.set(shiftId, group);
        }
    }

    return buildGroupsFromClients(selectedShifts, defaultShiftGroupMode).map(
        (group) => {
            const existingGroup = group.shiftIds
                .map((shiftId) => existingGroupsByShiftId.get(shiftId))
                .find((candidate) => candidate !== undefined);
            return existingGroup ? { ...group, id: existingGroup.id } : group;
        },
    );
}

// Distinct from `null`: the coordinator cleared every group, so the server
// must not apply its same-client default.
const NO_SHIFT_GROUPS: ReadonlyArray<ShiftGroup> = [];

function useShiftGroupingState(
    selectedShifts: Array<Shift>,
    defaultShiftGroupMode: ShiftGroupMode | undefined,
) {
    const [shiftGroupsValue, setShiftGroupsValue] = useState<
        ReadonlyArray<ShiftGroup>
    >([]);
    const [hasUserEditedGrouping, setHasUserEditedGrouping] = useState(false);
    // The auto-grouping effect must not re-run when the flag flips: it would
    // sanitize away a group the editor has just created and not yet filled.
    const hasUserEditedGroupingRef = useRef(false);

    useEffect(() => {
        setShiftGroupsValue((previousGroups) => {
            const sanitized = sanitizeShiftGroups(
                previousGroups,
                selectedShifts,
            );
            // Once the user has edited grouping, never auto-regroup over them.
            if (hasUserEditedGroupingRef.current) {
                return areShiftGroupsEqual(previousGroups, sanitized)
                    ? previousGroups
                    : sanitized;
            }

            if (defaultShiftGroupMode === undefined) {
                return [];
            }

            // Otherwise (re)build a default group for every same-client set as
            // the shift selection changes, preserving existing group ids.
            const nextGroups = buildDefaultShiftGroups(
                selectedShifts,
                sanitized,
                defaultShiftGroupMode,
            );
            return areShiftGroupsEqual(previousGroups, nextGroups)
                ? previousGroups
                : nextGroups;
        });
    }, [selectedShifts, defaultShiftGroupMode]);

    const setUserShiftGroupsValue = useCallback(
        (value: SetStateAction<ReadonlyArray<ShiftGroup>>) => {
            hasUserEditedGroupingRef.current = true;
            setHasUserEditedGrouping(true);
            setShiftGroupsValue(value);
        },
        [],
    );

    const resetShiftGroupsValue = useCallback(() => {
        hasUserEditedGroupingRef.current = false;
        setHasUserEditedGrouping(false);
        setShiftGroupsValue([]);
    }, []);

    return {
        shiftGroupsValue,
        setShiftGroupsValue: setUserShiftGroupsValue,
        resetShiftGroupsValue,
        activeShiftGroups:
            shiftGroupsValue.length > 0
                ? shiftGroupsValue
                : hasUserEditedGrouping
                  ? NO_SHIFT_GROUPS
                  : null,
    };
}

type ShiftEditorSectionProps = {
    readonly organization: Organization | undefined;
    readonly selectedShifts: Array<Shift>;
    readonly shiftGroupsValue: ReadonlyArray<ShiftGroup>;
    readonly onShiftGroupsChange: (value: ReadonlyArray<ShiftGroup>) => void;
    readonly collapseGroupingControls: boolean;
    readonly groupingTooltip?: string;
    readonly showGroupingControls: boolean;
    readonly onShowGroupingControlsChange: (value: boolean) => void;
    readonly showAutoAssign?: boolean;
    readonly autoAssignShiftIds?: ReadonlySet<string>;
    readonly autoAssignPolicy?: AutoAssignCaregiverPolicy;
    readonly onAutoAssignShiftIdsChange?: (
        value: SetStateAction<ReadonlySet<string>>,
    ) => void;
};

function ShiftEditorSection({
    organization,
    selectedShifts,
    shiftGroupsValue,
    onShiftGroupsChange,
    collapseGroupingControls,
    groupingTooltip,
    showGroupingControls,
    onShowGroupingControlsChange,
    showAutoAssign,
    autoAssignShiftIds,
    autoAssignPolicy,
    onAutoAssignShiftIdsChange,
}: ShiftEditorSectionProps) {
    if (selectedShifts.length === 0) {
        return null;
    }

    if (!collapseGroupingControls) {
        return (
            <ShiftGroupEditor
                shifts={selectedShifts}
                organization={organization}
                value={shiftGroupsValue}
                onChange={onShiftGroupsChange}
                showAutoAssign={showAutoAssign}
                autoAssignShiftIds={autoAssignShiftIds}
                autoAssignPolicy={autoAssignPolicy}
                onAutoAssignShiftIdsChange={onAutoAssignShiftIdsChange}
                showGroupingControls={true}
            />
        );
    }

    return (
        <Collapsible
            open={showGroupingControls}
            onOpenChange={onShowGroupingControlsChange}
            className="space-y-3"
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center">
                    <CollapsibleTrigger asChild={true}>
                        <Button type="button" variant="outline" size="sm">
                            {showGroupingControls
                                ? 'Hide groups'
                                : 'Group shifts'}
                        </Button>
                    </CollapsibleTrigger>
                    {groupingTooltip ? (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <button
                                    type="button"
                                    aria-label="Grouping guidance"
                                    className="text-muted-foreground hover:text-foreground inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors"
                                >
                                    <Info className="h-4 w-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                                {groupingTooltip}
                            </TooltipContent>
                        </Tooltip>
                    ) : null}
                </div>
            </div>
            {!showGroupingControls && showAutoAssign ? (
                <ShiftGroupEditor
                    shifts={selectedShifts}
                    organization={organization}
                    value={shiftGroupsValue}
                    onChange={onShiftGroupsChange}
                    showAutoAssign={showAutoAssign}
                    autoAssignShiftIds={autoAssignShiftIds}
                    autoAssignPolicy={autoAssignPolicy}
                    onAutoAssignShiftIdsChange={onAutoAssignShiftIdsChange}
                    showGroupingControls={false}
                />
            ) : null}
            <CollapsibleContent>
                <ShiftGroupEditor
                    shifts={selectedShifts}
                    organization={organization}
                    value={shiftGroupsValue}
                    onChange={onShiftGroupsChange}
                    showAutoAssign={showAutoAssign}
                    autoAssignShiftIds={autoAssignShiftIds}
                    autoAssignPolicy={autoAssignPolicy}
                    onAutoAssignShiftIdsChange={onAutoAssignShiftIdsChange}
                    showGroupingControls={true}
                />
            </CollapsibleContent>
        </Collapsible>
    );
}

export type ShiftOrgMatch = {
    readonly organizationId: string;
    readonly organizationName: string | null;
    readonly phoebeShiftId: string;
};

/**
 * Non-blocking picker surfaced at the top of the Create Outreach modal
 * when one EHR shift resolves in 2+ Phoebe orgs the caller belongs to.
 *
 * The modal opens in whichever org the caller landed on; this control
 * only adds an affordance to switch. Picking a different org calls back
 * into the caller, which is responsible for actually switching the
 * active org (usually via `setOrganizationId` + URL update) so the
 * modal's data re-fetches for the newly picked org.
 */
function ShiftOrganizationPicker({
    shiftOrgMatches,
    activeOrganizationId,
    onPickShiftOrganization,
}: {
    readonly shiftOrgMatches: ReadonlyArray<ShiftOrgMatch> | undefined;
    readonly activeOrganizationId: string | null;
    readonly onPickShiftOrganization?: (organizationId: string) => void;
}) {
    if (!shiftOrgMatches || shiftOrgMatches.length < 2) {
        return null;
    }

    const selectedValue = shiftOrgMatches.find(
        (match) => match.organizationId === activeOrganizationId,
    )?.organizationId;

    return (
        <div
            className="bg-muted/40 flex flex-col gap-2 rounded-md border p-3"
            data-testid="shift-org-picker"
        >
            <div className="flex flex-col gap-0.5">
                <Label htmlFor="shift-org-picker-select">
                    Which office is this shift in?
                </Label>
                <p className="text-muted-foreground text-sm">
                    This shift exists in {shiftOrgMatches.length} of your
                    offices. Pick one to create the outreach there.
                </p>
            </div>
            <Select
                value={selectedValue}
                onValueChange={(value) => {
                    onPickShiftOrganization?.(value);
                }}
            >
                <SelectTrigger
                    id="shift-org-picker-select"
                    className="w-full sm:max-w-sm"
                >
                    <SelectValue placeholder="Select office" />
                </SelectTrigger>
                <SelectContent>
                    {shiftOrgMatches.map((match) => (
                        <SelectItem
                            key={match.organizationId}
                            value={match.organizationId}
                        >
                            {match.organizationName ?? match.organizationId}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

type CreateOutreachModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly onSuccess?: (outreachId: string | null) => void;
    readonly navigateToOutreachOnManualSuccess?: boolean;
    readonly navigateToOutreachOnAgentSuccess?: boolean;
    readonly shiftIds?: Array<string>;
    readonly manualEntry?: ManualOutreachEntry;
    readonly analyticsContext?: OutreachCreationAnalyticsContext;
    // Edit mode: pre-populates the composer with the supplied outreach so
    // users can patch a pending outreach without a redundant fetch. See
    // CreateOutreachForm#editContext for submit semantics.
    readonly outreachToEdit?: JoinedOutreachRead;
    readonly onOutreachSaved?: () => void;
    // When the EHR shift that seeded this modal resolves in 2+ Phoebe orgs
    // the caller belongs to, `shiftOrgMatches` lists those orgs so the
    // modal can surface a non-blocking "which office?" picker above the
    // composer. Picking switches the active org via
    // `onPickShiftOrganization`; the URL/context churn is the caller's
    // responsibility. Leave undefined for the single-org case (no picker).
    readonly shiftOrgMatches?: ReadonlyArray<ShiftOrgMatch>;
    readonly onPickShiftOrganization?: (organizationId: string) => void;
};

const ALLOWED_OUTREACH_TEMPLATE_TYPES: Array<
    OutreachTemplate['outreach_type']
> = ['callout', 'recurring_shift'];

// Automatic ("agent") hands caregiver picking to Phoebe through the quick
// form; Manual renders the original composer with editable steps and
// messages and leaves picking to the coordinator.
type OutreachComposerMode = 'agent' | 'manual';

const normalizeShiftIds = (shiftIds: Array<string>): Array<string> =>
    Array.from(new Set(shiftIds));

const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHIFT_BATCH_MAX_IDS = 100;

/**
 * Read shifts by id through the batch endpoint, in chunks the endpoint
 * accepts.
 *
 * Never rejects: a chunk that fails reports its ids in `failedShiftIds` so a
 * caller can retry only those, while chunks that succeeded still contribute
 * their shifts. Ids the organization has no shift for simply come back in
 * neither list, since the endpoint omits them rather than failing.
 */
async function fetchShiftsBatch(
    organizationId: string,
    shiftIds: ReadonlyArray<string>,
): Promise<{
    readonly shifts: Array<Shift>;
    readonly failedShiftIds: Array<string>;
}> {
    const postBatch = (ids: ReadonlyArray<string>) =>
        getApiClient().post({
            url: '/organizations/{organization_id}/shifts/batch',
            pathParams: { organization_id: organizationId },
            queryParams: { include_active_fill_outreach: true },
            body: [...ids],
            idempotent: true,
        });

    const postBatchWithInvalidIdRecovery = async (
        ids: ReadonlyArray<string>,
    ) => {
        try {
            return await postBatch(ids);
        } catch (error) {
            const validShiftIds = ids.filter((shiftId) =>
                UUID_PATTERN.test(shiftId),
            );
            const canRecoverFromInvalidIds =
                error instanceof ApiError &&
                error.status === 422 &&
                validShiftIds.length < ids.length;
            if (!canRecoverFromInvalidIds) {
                throw error;
            }

            // Route params can be hand-edited. Preserve every valid selection
            // without falling back to one request per shift.
            return validShiftIds.length > 0 ? postBatch(validShiftIds) : [];
        }
    };

    const chunks: Array<Array<string>> = [];
    for (let index = 0; index < shiftIds.length; index += SHIFT_BATCH_MAX_IDS) {
        chunks.push(shiftIds.slice(index, index + SHIFT_BATCH_MAX_IDS));
    }
    const results = await Promise.allSettled(
        chunks.map(postBatchWithInvalidIdRecovery),
    );

    const shifts: Array<Shift> = [];
    const failedShiftIds: Array<string> = [];
    results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
            shifts.push(...result.value);
            return;
        }
        failedShiftIds.push(...(chunks[index] ?? []));
    });
    return { shifts, failedShiftIds };
}

export function CreateOutreachModal({
    isOpen,
    onClose,
    onSuccess,
    navigateToOutreachOnManualSuccess = true,
    navigateToOutreachOnAgentSuccess = true,
    shiftIds: shiftIdsProp,
    manualEntry: manualEntryProp,
    analyticsContext,
    outreachToEdit,
    onOutreachSaved,
    shiftOrgMatches,
    onPickShiftOrganization,
}: CreateOutreachModalProps) {
    const isEditMode = !!outreachToEdit;
    const manualEntry = isEditMode ? undefined : manualEntryProp;
    const isManualEntry = manualEntry !== undefined;
    const allowDiscoveryReplacement = !isEditMode;
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'create-outreach-modal',
    });
    const ehrDisplayName = organization?.general_settings?.primary_data_source
        ? getDataSourceDisplayName(
              organization.general_settings.primary_data_source,
          )
        : null;
    const { data: outreachTemplates = [], isLoading: isLoadingTemplates } =
        useOutreachTemplates(organizationId, {
            reason: 'create-outreach-modal',
        });
    const [selectedTemplateType, setSelectedTemplateType] =
        useState<OutreachTemplate['outreach_type']>('callout');
    const [selectedShiftIds, setSelectedShiftIdsState] = useState<
        Array<string>
    >(normalizeShiftIds(shiftIdsProp ?? []));
    const setSelectedShiftIds = useCallback(
        (value: SetStateAction<Array<string>>) => {
            setSelectedShiftIdsState((previousValue) =>
                normalizeShiftIds(
                    typeof value === 'function' ? value(previousValue) : value,
                ),
            );
        },
        [],
    );
    const [outreachComposerMode, setOutreachComposerMode] =
        useState<OutreachComposerMode>('manual');
    const [showGroupingControls, setShowGroupingControls] = useState(false);

    // Sync selectedShiftIds when the modal opens with shiftIds prop. Skip in
    // edit mode — seeding from the fetched outreach happens below.
    useEffect(() => {
        if (!isOpen || isEditMode) {
            return;
        }

        const ids = shiftIdsProp ?? [];
        if (ids.length > 0) {
            setSelectedTemplateType('callout');
            setSelectedShiftIds(ids);
        } else {
            setSelectedShiftIds([]);
        }
    }, [isOpen, isEditMode, setSelectedShiftIds, shiftIdsProp]);

    // Track client IDs whose care plan summary was just saved in this session.
    // Used to optimistically hide the missing-care-plan banner immediately after
    // saving, without waiting for the shifts SWR re-fetch to return the new flag.
    const [savedCarePlanClientIds, setSavedCarePlanClientIds] = useState<
        ReadonlySet<string>
    >(new Set());
    const [carePlanSummary, setCarePlanSummary] = useState<string>('');
    const [carePlanBullets, setCarePlanBullets] = useState<string>('');
    const [perShiftCarePlans, setPerShiftCarePlans] = useState<
        Record<string, { summary: string; bullets: string }>
    >({});
    const [isLoadingCarePlan, setIsLoadingCarePlan] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const isLoadingMoreRef = useRef(false);
    // Track shifts discovered via search that aren't in paginated results
    const [discoveredShifts, setDiscoveredShifts] = useState<Array<Shift>>([]);
    const discoveredShiftIdsRef = useRef<Set<string>>(new Set());
    // Ids the current modal session has already sent to the batch endpoint.
    // The set is replaced per session, so its identity also serves as the
    // session token that keeps a response from a previous open (or a previous
    // organization) out of the current selection.
    const attemptedShiftIdsRef = useRef<Set<string>>(new Set());

    const requireCarePlanAcknowledgement = useFeatureFlag(
        'require_care_plan_acknowledgement',
        organization?.features,
    );
    const agentV3Enabled = useFeatureFlag(
        'agent_v3_enabled',
        organization?.features,
    );
    const autoAssignAllowed = isAutoAssignAllowed(
        organization?.callout_settings,
    );
    const organizationAutoAssignFirstResponderDefault =
        organization?.callout_settings?.auto_assign_first_responder_default ??
        false;
    const [autoAssignShiftIds, setAutoAssignShiftIds] = useState<
        ReadonlySet<string>
    >(new Set());
    const [autoAssignTouched, setAutoAssignTouched] = useState(false);
    // Shift ids whose auto-assign checkbox has already been seeded from the
    // client/org default, so a later data load never re-applies a default
    // over a coordinator's manual choice.
    const autoAssignSeededShiftIdsRef = useRef<Set<string>>(new Set());
    const [autoAssignNotifyNonResponders, setAutoAssignNotifyNonResponders] =
        useState(true);
    const [autoAssignModifyReasonId, setAutoAssignModifyReasonId] = useState<
        number | null
    >(null);
    const autoAssignFirstResponderEnabled = autoAssignShiftIds.size > 0;
    const manualOutreachEnabled =
        organization?.callout_settings?.manual_outreach_enabled === true;
    // Pick the tab once per composer session. Switching the outreach type
    // must not reset it: that would unmount the Manual form and discard the
    // steps and messages the coordinator has already edited.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setOutreachComposerMode(
            isEditMode || isManualEntry ? 'manual' : 'agent',
        );
    }, [isOpen, isEditMode, isManualEntry]);

    const selectedTemplate = useMemo(
        () =>
            outreachTemplates.find(
                (template) => template.outreach_type === selectedTemplateType,
            ),
        [outreachTemplates, selectedTemplateType],
    );
    const supportsAutoAssignFirstResponder =
        autoAssignAllowed &&
        supportsOutreachAutoAssignFirstResponder(selectedTemplateType);
    // Let HHAExchange schedulers pick the modify reason recorded on the billing
    // edit when a first responder is auto-assigned, instead of the provider's
    // arbitrary first-active-reason default. Create-only, mirroring the
    // auto-assign notify control below.
    const mode = useMode();
    const isSandboxMode = mode === 'sandbox';
    const shouldFetchAutoAssignModifyReasons =
        supportsAutoAssignFirstResponder &&
        !isEditMode &&
        autoAssignFirstResponderEnabled &&
        organization?.general_settings?.primary_data_source === 'hhaexchange' &&
        !isSandboxMode;
    const {
        data: autoAssignModifyReasonsData,
        isLoading: isLoadingAutoAssignModifyReasons,
        error: autoAssignModifyReasonsError,
    } = useOrgHHAExchangeModifyReasons(
        organizationId,
        { reason: 'CreateOutreachModalAutoAssign' },
        { enabled: shouldFetchAutoAssignModifyReasons },
    );
    const shouldRenderAutoAssignModifyReason =
        shouldFetchAutoAssignModifyReasons;
    const PAGE_SIZE = 400;
    const MAX_SHIFTS = 4000;
    const SHIFT_WINDOW_DAYS = 14;
    const [offset, setOffset] = useState(0);
    const [allShifts, setAllShifts] = useState<Array<Shift>>([]);
    // Track which offset we've already accumulated to prevent duplicate appends
    // when the effect runs with stale shiftsData (due to keepPreviousData in SWR)
    const lastAccumulatedOffset = useRef<number>(-1);

    const addDiscoveredShift = useCallback((shift: Shift) => {
        discoveredShiftIdsRef.current.add(shift.id);
        setDiscoveredShifts((prev) => {
            if (prev.some((existingShift) => existingShift.id === shift.id)) {
                return prev;
            }
            return [...prev, shift];
        });
    }, []);

    const startTimestamp = useMemo(
        () => (isOpen ? new Date().toISOString() : null),
        [isOpen],
    );

    const shiftQuery = useMemo(() => {
        if (!startTimestamp) {
            return undefined;
        }

        const endTimestamp = new Date(
            new Date(startTimestamp).getTime() +
                SHIFT_WINDOW_DAYS * 24 * 60 * 60 * 1000,
        ).toISOString();

        return {
            shift_start_date: startTimestamp,
            shift_end_date: endTimestamp,
            order: 'asc' as const,
            limit: PAGE_SIZE,
            offset,
            // Lets the picker surface active holders and apply the same
            // create-mode block-or-replace rules client-side.
            include_active_fill_outreach: true,
        };
    }, [offset, startTimestamp]);
    const {
        data: shiftsData,
        isLoading: isLoadingShifts,
        mutate: mutateShifts,
    } = useShifts(
        isOpen ? organizationId : null,
        { reason: 'create-outreach-modal' },
        shiftQuery,
    );

    // A new session — an open, or an organization switch while open — starts
    // its hydration from scratch and orphans any request the last one left.
    useEffect(() => {
        attemptedShiftIdsRef.current = new Set();
    }, [isOpen, organizationId]);

    // Selected shifts can sit outside the 14-day list window (deep links,
    // an existing outreach's selection), so hydrate whatever the list will
    // never contain in one batch instead of one request per shift.
    useEffect(() => {
        if (!isOpen || !organizationId) {
            return;
        }

        // `shiftsData` is included because the page it holds is only appended
        // to `allShifts` by the accumulation effect below, one commit later.
        const knownShiftIds = new Set([
            ...allShifts.map((shift) => shift.id),
            ...(shiftsData?.shifts ?? []).map((shift) => shift.id),
            ...discoveredShifts.map((shift) => shift.id),
        ]);
        // Already-attempted ids stay out of the request so an arriving list
        // page cannot start an overlapping batch for the same selection.
        const attemptedShiftIds = attemptedShiftIdsRef.current;
        const missingSelectedShiftIds = selectedShiftIds.filter(
            (shiftId) =>
                !knownShiftIds.has(shiftId) && !attemptedShiftIds.has(shiftId),
        );
        if (missingSelectedShiftIds.length === 0) {
            return;
        }
        missingSelectedShiftIds.forEach((shiftId) =>
            attemptedShiftIds.add(shiftId),
        );

        const hydrateSelectedShifts = async () => {
            const { shifts, failedShiftIds } = await fetchShiftsBatch(
                organizationId,
                missingSelectedShiftIds,
            );
            if (attemptedShiftIdsRef.current !== attemptedShiftIds) {
                return;
            }

            // Release only the ids no request answered for. They are retried
            // the next time the selection or the loaded shifts change, rather
            // than on every re-render. Ids the endpoint simply omitted stay
            // attempted: the shift is deleted or inaccessible, and the
            // composer stays usable without it.
            failedShiftIds.forEach((shiftId) =>
                attemptedShiftIds.delete(shiftId),
            );
            shifts.forEach(addDiscoveredShift);
        };

        void hydrateSelectedShifts();
    }, [
        addDiscoveredShift,
        allShifts,
        discoveredShifts,
        isOpen,
        organizationId,
        selectedShiftIds,
        shiftsData,
    ]);

    // Accumulate shifts as pages load
    // We track lastAccumulatedOffset to prevent double-appending when the effect
    // fires with stale shiftsData (due to keepPreviousData in SWR)
    useEffect(() => {
        if (!shiftsData?.shifts) {
            return;
        }

        if (offset === 0 && lastAccumulatedOffset.current !== 0) {
            setAllShifts(shiftsData.shifts);
            lastAccumulatedOffset.current = 0;
            isLoadingMoreRef.current = false;
        } else if (offset > lastAccumulatedOffset.current) {
            // Only append if this is a new page we haven't processed yet
            // Use functional update to check for stale data without adding allShifts to deps
            setAllShifts((prev) => {
                const firstShiftId = shiftsData.shifts[0]?.id;
                const isStaleData =
                    firstShiftId && prev.some((s) => s.id === firstShiftId);

                if (isStaleData) {
                    return prev;
                }

                lastAccumulatedOffset.current = offset;
                const newShifts = [...prev, ...shiftsData.shifts];
                isLoadingMoreRef.current = false;
                return newShifts;
            });
        }
    }, [shiftsData, offset]);

    // Reset pagination when modal closes
    useEffect(() => {
        if (!isOpen) {
            setOffset(0);
            setAllShifts([]);
            lastAccumulatedOffset.current = -1;
            isLoadingMoreRef.current = false;
            setDiscoveredShifts([]);
            discoveredShiftIdsRef.current = new Set();
            setRejectedCancelledShiftIds(new Set());
        }
    }, [isOpen]);

    const hasMoreShifts =
        (shiftsData?.total ?? 0) > allShifts.length &&
        allShifts.length < MAX_SHIFTS;

    const loadMoreShifts = () => {
        if (hasMoreShifts && !isLoadingShifts && !isLoadingMoreRef.current) {
            isLoadingMoreRef.current = true;
            setOffset((prev) => prev + PAGE_SIZE);
        }
    };

    const filteredShifts = useMemo(() => {
        const shifts = Array.from(
            new Map(allShifts.map((shift) => [shift.id, shift])).values(),
        );
        const now = new Date().toISOString();

        // Include shifts discovered via search or pre-selection that aren't in paginated results
        for (const discoveredShift of discoveredShifts) {
            if (!shifts.some((s) => s.id === discoveredShift.id)) {
                shifts.push(discoveredShift);
            }
        }

        // Keep stale preselected cancellations visible so the coordinator can
        // remove them, while excluding cancelled shifts from new selections.
        const activeShifts = shifts.filter((shift) => {
            const isSelectedCancellation =
                selectedShiftIds.includes(shift.id) && isCancelledShift(shift);
            return (
                isSelectedCancellation ||
                (shift.shift_end_time > now && !isCancelledShift(shift))
            );
        });

        return activeShifts;
    }, [allShifts, discoveredShifts, selectedShiftIds]);

    const selectedShifts = useMemo(() => {
        const shiftById = new Map(
            filteredShifts.map((shift) => [shift.id, shift]),
        );
        // Include pre-selected shifts from discoveredShifts that didn't
        // survive the active-shift filter (e.g. missing shifts with past
        // end times) so we can detect and surface their status.
        for (const shift of discoveredShifts) {
            if (
                !shiftById.has(shift.id) &&
                selectedShiftIds.includes(shift.id)
            ) {
                shiftById.set(shift.id, shift);
            }
        }
        return selectedShiftIds.flatMap((shiftId) => {
            const shift = shiftById.get(shiftId);
            return shift ? [shift] : [];
        });
    }, [filteredShifts, selectedShiftIds, discoveredShifts]);
    const handleAutoAssignShiftIdsChange = useCallback(
        (value: SetStateAction<ReadonlySet<string>>) => {
            setAutoAssignTouched(true);
            // Once the coordinator edits the checkboxes, every shift on
            // screen is their call: a client policy that resolves later must
            // not re-check a shift they left off. Shifts added afterwards
            // still get their default.
            for (const shift of selectedShifts) {
                autoAssignSeededShiftIdsRef.current.add(shift.id);
            }
            setAutoAssignShiftIds(value);
        },
        [selectedShifts],
    );

    const selectedClientIds = useMemo(
        () =>
            [
                ...new Set(
                    selectedShifts.flatMap((shift) =>
                        shift.client_id ? [shift.client_id] : [],
                    ),
                ),
            ].sort(),
        [selectedShifts],
    );
    // Client rows carry the per-client auto-assign preference used to seed
    // each shift's auto-assign checkbox below.
    const {
        data: clients,
        error: clientsError,
        isLoading: isLoadingClients,
    } = useOrganizationClients(
        isOpen && supportsAutoAssignFirstResponder ? organizationId : null,
        {
            reason: 'create-outreach-modal',
        },
        { includeInactive: true, clientIds: selectedClientIds },
    );
    const clientById = useMemo(() => {
        const map = new Map<string, OrgClientRead>();
        for (const client of clients ?? []) {
            map.set(client.id, client);
        }
        return map;
    }, [clients]);
    const autoAssignDefaultsResolved = !isLoadingClients && !clientsError;
    const autoAssignShiftIdsForSubmit =
        autoAssignDefaultsResolved ||
        autoAssignTouched ||
        autoAssignShiftIds.size > 0
            ? autoAssignShiftIds
            : null;

    // Effective multi-shift cap for the current selection: same-client
    // selections get the higher cap. A selected id we cannot resolve to a
    // shift has an unknown client, so the base cap applies.
    const selectedShiftLimit = useMemo(() => {
        if (selectedShifts.length < selectedShiftIds.length) {
            return MULTI_SHIFT_OUTREACH_MAX;
        }
        return multiShiftOutreachMax(
            selectedShifts.map((shift) => shift.client_id ?? null),
        );
    }, [selectedShifts, selectedShiftIds]);

    // Pre-selected shifts (deep link / @mention / "Create outreach" from a
    // stale row) bypass the combobox MISSING strict-filter, so the modal can
    // open with a missing shift selected. Surface the same banner used on
    // the outreach detail / list pages so the coordinator sees the EHR
    // mismatch before they hit submit.
    const missingSelectedShifts = useMemo(
        () => selectedShifts.filter((shift) => shift.status === 'missing'),
        [selectedShifts],
    );
    // Shift ids the server rejected as cancelled. Held separately from the
    // refetched rows because the refetch can fail (network, a hard-deleted
    // shift 404ing, a transient 5xx). Deriving the block from refetched rows
    // alone would fail open: nothing would change, submit would stay enabled,
    // and the coordinator would loop on the same rejection. The server's
    // answer is authoritative, so it holds the block on its own and the
    // refetch only enriches what is displayed.
    const [rejectedCancelledShiftIds, setRejectedCancelledShiftIds] = useState<
        ReadonlySet<string>
    >(new Set());
    const cancelledSelectedShifts = useMemo(
        () =>
            selectedShifts.filter(
                (shift) =>
                    isCancelledShift(shift) ||
                    rejectedCancelledShiftIds.has(shift.id),
            ),
        [selectedShifts, rejectedCancelledShiftIds],
    );
    // A rejected id can also drop out of `selectedShifts` entirely (hard
    // delete), so count ids still selected rather than only resolved rows.
    const blockedCancelledCount = useMemo(() => {
        const ids = new Set(cancelledSelectedShifts.map((shift) => shift.id));
        for (const shiftId of selectedShiftIds) {
            if (rejectedCancelledShiftIds.has(shiftId)) {
                ids.add(shiftId);
            }
        }
        return ids.size;
    }, [cancelledSelectedShifts, rejectedCancelledShiftIds, selectedShiftIds]);
    // Missing shifts stay visible in the modal so the coordinator can remove
    // them, but they are stripped from the create payload because the server
    // can no longer target them. Held-shift blocking must match that payload.
    const nonMissingSelectedShifts = useMemo(
        () => selectedShifts.filter((shift) => shift.status !== 'missing'),
        [selectedShifts],
    );
    const blockedHeldCount = useMemo(
        () =>
            new Set(
                nonMissingSelectedShifts
                    .filter((shift) =>
                        isShiftHeldByAnotherOutreach(shift, {
                            allowDiscoveryReplacement,
                        }),
                    )
                    .map((shift) => shift.id),
            ).size,
        [allowDiscoveryReplacement, nonMissingSelectedShifts],
    );
    const creationBlockedReason =
        manualEntry?.validateSelection(selectedShiftIds) ??
        (!isEditMode && blockedCancelledCount > 0
            ? blockedCancelledCount === 1
                ? 'This shift is cancelled. Remove it from the selection to continue.'
                : 'One or more selected shifts are cancelled. Remove them from the selection to continue.'
            : !isEditMode && blockedHeldCount > 0
              ? blockedHeldCount === 1
                  ? 'This shift is already in an active outreach. Remove it from the selection to continue.'
                  : 'One or more selected shifts are already in an active outreach. Remove them from the selection to continue.'
              : !isEditMode &&
                  supportsAutoAssignFirstResponder &&
                  !autoAssignTouched &&
                  (!autoAssignDefaultsResolved ||
                      selectedClientIds.some((id) => !clientById.has(id)))
                ? isLoadingClients
                    ? 'Loading client auto-assign settings…'
                    : 'Client auto-assign settings could not be loaded. Reopen this outreach to try again, or set the auto-assign checkboxes yourself.'
                : undefined);

    const nonMissingSelectedShiftIds = useMemo(
        () => nonMissingSelectedShifts.map((shift) => shift.id),
        [nonMissingSelectedShifts],
    );

    // Shifts whose client has no care plan data anywhere (no raw care plan,
    // no saved client summary). Surfaced pre-send so the coordinator can add
    // a quick summary before the first messages go out empty. Prefers the
    // `client_has_care_plan` flag (list rows no longer carry the full
    // care-plan text) but falls back to the text so a backend that predates
    // the flag doesn't make every shift look care-plan-less mid-rollout.
    const shiftsMissingCarePlanData = useMemo(
        () =>
            nonMissingSelectedShifts.filter(
                (shift) =>
                    (shift.client_has_care_plan ??
                        Boolean(shift.patient_care_plan?.trim())) !== true &&
                    shift.client_has_care_plan_summary !== true &&
                    // Optimistic client-level dismiss after saving.
                    !savedCarePlanClientIds.has(shift.client_id ?? '') &&
                    // Also exclude once a summary was just generated for this
                    // shift (before the SWR re-fetch returns the new flag).
                    !perShiftCarePlans[shift.id],
            ),
        [nonMissingSelectedShifts, perShiftCarePlans, savedCarePlanClientIds],
    );

    const defaultShiftGroupMode = resolveAutomaticShiftGroupMode(
        selectedTemplateType,
        organization?.callout_settings,
    );
    const {
        shiftGroupsValue,
        setShiftGroupsValue,
        resetShiftGroupsValue,
        activeShiftGroups,
    } = useShiftGroupingState(
        nonMissingSelectedShifts,
        // An edit seeds stored groups; defaults would replace them.
        isEditMode ? undefined : defaultShiftGroupMode,
    );
    const groupedSelectedShifts = useMemo(
        () =>
            orderShiftsForOutreach(nonMissingSelectedShifts, activeShiftGroups),
        [activeShiftGroups, nonMissingSelectedShifts],
    );

    const isStaffingOutreachTemplateSelected =
        selectedTemplateType === 'callout' ||
        selectedTemplateType === 'recurring_shift';
    // No outreach row exists yet, so mirror the creation-time generation rule
    // (org flag plus a callout-family type) to pick the chrome the detail page
    // will use once the outreach is created.
    const chrome: OutreachDetailChrome =
        agentV3Enabled && isStaffingOutreachTemplateSelected
            ? 'compact'
            : 'default';
    // With manual outreach disabled — or not yet known, because the backend
    // rejects a manual shift-fill creation the composer cannot see coming —
    // the Automatic/Manual tabs go away and the composer always uses the
    // quick (agent) flow.
    const showOutreachModeTabs =
        !isManualEntry &&
        !isEditMode &&
        isStaffingOutreachTemplateSelected &&
        manualOutreachEnabled;
    const shouldUseQuickOutreachForm =
        !isManualEntry &&
        !isEditMode &&
        isStaffingOutreachTemplateSelected &&
        (outreachComposerMode === 'agent' || !manualOutreachEnabled);

    const templateOptions = useMemo(
        () => [
            ...outreachTemplates
                .filter((template) =>
                    ALLOWED_OUTREACH_TEMPLATE_TYPES.includes(
                        template.outreach_type,
                    ),
                )
                .map((template) => ({
                    label: getLabelForOutreachType(template.outreach_type),
                    value: template.outreach_type,
                })),
        ],
        [outreachTemplates],
    );

    // Re-read specific shifts from the API and overwrite the copies the
    // composer holds. Used when the create endpoint rejects a submission
    // because our shift data went stale; `mutateShifts` alone would not
    // help, since the accumulated `allShifts` pages and the pre-selected
    // `discoveredShifts` are each only populated once per open.
    const refreshShiftsById = useCallback(
        async (shiftIds: ReadonlyArray<string>) => {
            if (!organizationId || shiftIds.length === 0) {
                return;
            }

            const { shifts: refreshed } = await fetchShiftsBatch(
                organizationId,
                shiftIds,
            );
            if (refreshed.length === 0) {
                return;
            }

            const refreshedById = new Map(
                refreshed.map((shift) => [shift.id, shift]),
            );
            // Release the server-seeded block for any shift that comes back
            // not cancelled. AxisCare cancellations can be transient and
            // self-healing, and without this the coordinator would have to
            // close and reopen the composer to recover.
            setRejectedCancelledShiftIds((previous) => {
                const next = new Set(previous);
                for (const shift of refreshed) {
                    if (!isCancelledShift(shift)) {
                        next.delete(shift.id);
                    }
                }
                return next.size === previous.size ? previous : next;
            });
            const replaceStale = (shifts: Array<Shift>) =>
                shifts.map((shift) => refreshedById.get(shift.id) ?? shift);
            setAllShifts(replaceStale);
            setDiscoveredShifts((previous) => {
                const updated = replaceStale(previous);
                const known = new Set(updated.map((shift) => shift.id));
                const added = refreshed.filter((shift) => !known.has(shift.id));
                for (const shift of added) {
                    discoveredShiftIdsRef.current.add(shift.id);
                }
                return added.length > 0 ? [...updated, ...added] : updated;
            });
        },
        [organizationId],
    );

    const hasShifts = filteredShifts.length > 0;
    const handleShiftDiscovered = (shift: Shift) => {
        addDiscoveredShift(shift);
    };
    const handleClose = () => {
        onClose();
        setSelectedTemplateType('callout');
        setSelectedShiftIds(shiftIdsProp ?? []);
        setAutoAssignShiftIds(new Set());
        setAutoAssignTouched(false);
        autoAssignSeededShiftIdsRef.current = new Set();
        setAutoAssignNotifyNonResponders(true);
        setAutoAssignModifyReasonId(null);
        resetShiftGroupsValue();
        setShowGroupingControls(false);
        setSavedCarePlanClientIds(new Set());
    };

    // Edit mode is seeded once from the saved outreach below; re-initializing
    // here would clobber that seed when the org read resolves after open.
    useEffect(() => {
        if (!isOpen || isEditMode) {
            return;
        }

        setAutoAssignShiftIds(new Set());
        setAutoAssignTouched(false);
        autoAssignSeededShiftIdsRef.current = new Set();
        setAutoAssignNotifyNonResponders(true);
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- only re-initialize when the modal opens or org settings change, not on every shift/group change
    }, [
        isOpen,
        isEditMode,
        organizationAutoAssignFirstResponderDefault,
        supportsAutoAssignFirstResponder,
        autoAssignAllowed,
    ]);

    // Seed each selected shift's auto-assign checkbox from its client's
    // Per-client auto-assign policy, falling back to the org default.
    // Seeding is per shift and one-shot: the ref of already-seeded ids keeps
    // manual un-checks from being re-applied when shift or client data loads
    // later, and covers shifts added after the modal opened. A shift whose
    // row has not resolved yet is left for a later pass so its client
    // preference is not missed.
    useEffect(() => {
        if (
            !isOpen ||
            !supportsAutoAssignFirstResponder ||
            isLoadingClients ||
            clientsError
        ) {
            return;
        }

        const seeded = autoAssignSeededShiftIdsRef.current;
        const shiftIdsToTurnOn: Array<string> = [];
        for (const shift of selectedShifts) {
            if (seeded.has(shift.id)) {
                continue;
            }
            const clientId = shift.client_id;
            const hasClient = clientId !== null && clientId !== undefined;
            const client = hasClient ? clientById.get(clientId) : undefined;
            if (hasClient && client === undefined) {
                continue;
            }
            seeded.add(shift.id);
            if (
                resolveClientAutoAssignDefault({
                    clientAutoAssignPolicy: client?.auto_assign_policy,
                    organizationAutoAssignFirstResponderDefault,
                })
            ) {
                shiftIdsToTurnOn.push(shift.id);
            }
        }
        if (shiftIdsToTurnOn.length === 0) {
            return;
        }

        setAutoAssignShiftIds((current) => {
            const next = new Set(current);
            for (const id of shiftIdsToTurnOn) {
                next.add(id);
            }
            return next.size === current.size ? current : next;
        });
    }, [
        selectedShifts,
        clientById,
        clientsError,
        isLoadingClients,
        isOpen,
        supportsAutoAssignFirstResponder,
        organizationAutoAssignFirstResponderDefault,
    ]);

    useEffect(() => {
        if (!autoAssignFirstResponderEnabled) {
            setAutoAssignNotifyNonResponders(false);
        } else {
            setAutoAssignNotifyNonResponders(true);
        }
    }, [autoAssignFirstResponderEnabled]);

    // Once shift data loads, trim any missing-status shifts from the
    // auto-assign set so they don't get sent with the outreach.
    useEffect(() => {
        if (missingSelectedShifts.length === 0) {
            return;
        }
        const missingIds = new Set(
            missingSelectedShifts.map((shift) => shift.id),
        );
        setAutoAssignShiftIds((prev) => {
            const next = new Set([...prev].filter((id) => !missingIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [missingSelectedShifts]);

    // Stable string key for nonMissingSelectedShiftIds — used as an effect
    // dependency instead of the array itself to avoid re-fires from
    // referentially-unstable memo-derived arrays.
    const nonMissingIdsKey = nonMissingSelectedShiftIds.join(',');
    const nonMissingIdsRef = useRef(nonMissingSelectedShiftIds);
    nonMissingIdsRef.current = nonMissingSelectedShiftIds;

    // Immediately invalidate stale metadata when shift selection changes
    const previousShiftIdsRef = useRef<string>('');
    useEffect(() => {
        if (previousShiftIdsRef.current !== nonMissingIdsKey) {
            const hadPreviousSelection = previousShiftIdsRef.current.length > 0;
            previousShiftIdsRef.current = nonMissingIdsKey;

            if (hadPreviousSelection) {
                setCarePlanSummary('');
                setCarePlanBullets('');
                setPerShiftCarePlans({});
                // Shift selection changed — clear the optimistic dismiss set so
                // the missing-care-plan banner re-evaluates against the new
                // shift list instead of staying hidden for previously-saved clients.
                setSavedCarePlanClientIds(new Set());
            }
        }
    }, [nonMissingIdsKey]);

    // Load care plan summary when shifts are selected and feature is enabled.
    // Skip for the automatic outreach form — care plan is generated post-creation.
    // Skip in edit mode too — seeded from outreach metadata below.
    useEffect(() => {
        if (isEditMode) {
            return;
        }
        if (shouldUseQuickOutreachForm || !requireCarePlanAcknowledgement) {
            setCarePlanSummary('');
            setCarePlanBullets('');
            setPerShiftCarePlans({});
            setSavedCarePlanClientIds(new Set());
            setIsLoadingCarePlan(false);
            return;
        }

        if (nonMissingIdsKey.length === 0) {
            setCarePlanSummary('');
            setCarePlanBullets('');
            setPerShiftCarePlans({});
            setSavedCarePlanClientIds(new Set());
            setIsLoadingCarePlan(false);
            return;
        }

        if (!organizationId) {
            return;
        }

        const shiftIds = nonMissingIdsRef.current;
        let isActive = true;
        const loadCarePlanSummary = async () => {
            setIsLoadingCarePlan(true);
            try {
                const response = await getApiClient().post({
                    url: '/organizations/{organization_id}/shifts/summarize-care-plan',
                    pathParams: {
                        organization_id: organizationId,
                    },
                    body: {
                        shift_ids: shiftIds,
                    },
                });

                if (!isActive) {
                    return;
                }

                setCarePlanSummary(response.summary || '');
                setCarePlanBullets(response.care_plan_bullets || '');
                const perShift: Record<
                    string,
                    { summary: string; bullets: string }
                > = {};
                for (const item of response.per_shift ?? []) {
                    perShift[item.shift_id] = {
                        summary: item.summary,
                        bullets: item.care_plan_bullets,
                    };
                }
                setPerShiftCarePlans(perShift);
            } catch (error) {
                if (isActive) {
                    console.error('Failed to fetch care plan summary:', error);
                    toast.error('Failed to generate care plan summary', {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                }
            } finally {
                if (isActive) {
                    setIsLoadingCarePlan(false);
                }
            }
        };

        loadCarePlanSummary();

        return () => {
            isActive = false;
        };
    }, [
        isEditMode,
        organizationId,
        nonMissingIdsKey,
        requireCarePlanAcknowledgement,
        shouldUseQuickOutreachForm,
    ]);

    // One-shot seed of editable state from the fetched outreach.
    const seededEditIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!isOpen || !isEditMode || !outreachToEdit) {
            return;
        }
        if (seededEditIdRef.current === outreachToEdit.id) {
            return;
        }
        seededEditIdRef.current = outreachToEdit.id;

        setSelectedTemplateType(outreachToEdit.outreach_type);
        const editShiftIds = outreachToEdit.shift_ids.map((id) => String(id));
        setSelectedShiftIds(editShiftIds);
        // The saved outreach owns these shifts' auto-assign choices, so mark
        // them seeded and let the default-seeding effect touch only shifts the
        // coordinator adds during the edit.
        autoAssignSeededShiftIdsRef.current = new Set(editShiftIds);
        setAutoAssignShiftIds(new Set(getAutoAssignShiftIds(outreachToEdit)));
        const carePlan = readCarePlanFromMetadata(outreachToEdit);
        setCarePlanSummary(carePlan.carePlanSummary);
        setCarePlanBullets(carePlan.carePlanBullets);
        setPerShiftCarePlans(
            carePlan.perShiftCarePlans as Record<
                string,
                { summary: string; bullets: string }
            >,
        );
    }, [isOpen, isEditMode, outreachToEdit, setSelectedShiftIds]);

    // Seed shift groups only once the selected shifts have loaded — otherwise
    // useShiftGroupingState's sanitize effect strips groups whose shifts are
    // not yet known, and may also auto-group-by-client on top.
    const seededGroupsIdRef = useRef<string | null>(null);
    const loadedShiftGroups = useMemo<ReadonlyArray<ShiftGroup>>(
        () =>
            (outreachToEdit?.shift_groups ?? []).map((group) => ({
                id: String(group.id),
                groupMode: group.group_mode,
                shiftIds: group.shift_ids.map((id) => String(id)),
            })),
        [outreachToEdit],
    );
    useEffect(() => {
        if (!isOpen || !isEditMode || !outreachToEdit) {
            return;
        }
        if (seededGroupsIdRef.current === outreachToEdit.id) {
            return;
        }
        const knownShiftIds = new Set(selectedShifts.map((shift) => shift.id));
        const allShiftsAvailable = outreachToEdit.shift_ids.every((id) =>
            knownShiftIds.has(String(id)),
        );
        if (!allShiftsAvailable) {
            return;
        }
        seededGroupsIdRef.current = outreachToEdit.id;
        setShiftGroupsValue(loadedShiftGroups);
    }, [
        isOpen,
        isEditMode,
        outreachToEdit,
        loadedShiftGroups,
        selectedShifts,
        setShiftGroupsValue,
    ]);

    // Reset the seed tracking so re-opening edit fetches fresh state.
    useEffect(() => {
        if (!isOpen) {
            seededEditIdRef.current = null;
            seededGroupsIdRef.current = null;
        }
    }, [isOpen]);

    // Single-shift outreaches show top-level care_plan_summary/bullets in the
    // preview but persist edits via shift_care_plans on edit-mode PATCH, so
    // mirror top-level edits into the per-shift map for the lone shift.
    const handleCarePlanSummaryChange = useCallback((value: string) => {
        setCarePlanSummary(value);
        const ids = nonMissingIdsRef.current;
        if (ids.length === 1) {
            const [shiftId] = ids;
            if (shiftId) {
                setPerShiftCarePlans((prev) => ({
                    ...prev,
                    [shiftId]: {
                        summary: value,
                        bullets: prev[shiftId]?.bullets ?? '',
                    },
                }));
            }
        }
    }, []);
    const handleCarePlanBulletsChange = useCallback((value: string) => {
        setCarePlanBullets(value);
        const ids = nonMissingIdsRef.current;
        if (ids.length === 1) {
            const [shiftId] = ids;
            if (shiftId) {
                setPerShiftCarePlans((prev) => ({
                    ...prev,
                    [shiftId]: {
                        summary: prev[shiftId]?.summary ?? '',
                        bullets: value,
                    },
                }));
            }
        }
    }, []);
    const handlePerShiftCarePlanChange = useCallback(
        (shiftId: string, plan: { summary: string; bullets: string }) => {
            setPerShiftCarePlans((prev) => ({
                ...prev,
                [shiftId]: plan,
            }));
        },
        [],
    );

    const editContext = useMemo(() => {
        if (!isEditMode || !outreachToEdit) {
            return undefined;
        }
        const initialFormValues = buildEditFormSeed(outreachToEdit);
        return {
            outreachId: outreachToEdit.id,
            originalOutreachType: outreachToEdit.outreach_type,
            initialIsMultiShift: outreachToEdit.shift_ids.length > 1,
            initialFormValues,
            initialSteps: outreachToEdit.steps,
            initialShiftGroups: loadedShiftGroups,
            isV3Born: outreachToEdit.agent_generation === 'v3',
            onSaved: onOutreachSaved,
        };
    }, [isEditMode, outreachToEdit, loadedShiftGroups, onOutreachSaved]);

    return (
        <React.Fragment>
            <Dialog
                open={isOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        handleClose();
                    }
                }}
            >
                <DialogContent className="flex flex-col overflow-hidden max-sm:fixed max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-[100dvw] max-sm:max-w-[100dvw] max-sm:translate-x-0 max-sm:translate-y-0 max-sm:overflow-hidden max-sm:rounded-none max-sm:border-0 max-sm:p-4 sm:max-h-[90vh] sm:max-w-[800px]">
                    <DialogHeader className="flex-shrink-0 space-y-1">
                        <DialogTitle>
                            {isEditMode
                                ? 'Edit Outreach'
                                : isManualEntry
                                  ? manualEntry.title
                                  : 'Create an Outreach'}
                        </DialogTitle>
                        {isEditMode ? (
                            <DialogDescription>
                                Update the outreach template, shifts, or
                                messaging before it starts.
                            </DialogDescription>
                        ) : null}
                        {manualEntry ? (
                            <DialogDescription>
                                {manualEntry.description}
                            </DialogDescription>
                        ) : null}
                    </DialogHeader>
                    <ShiftOrganizationPicker
                        shiftOrgMatches={shiftOrgMatches}
                        activeOrganizationId={organizationId}
                        onPickShiftOrganization={onPickShiftOrganization}
                    />
                    {missingSelectedShifts.length > 0 && (
                        <MissingShiftWarningBanner
                            missingShifts={missingSelectedShifts}
                            organizationId={organizationId ?? null}
                            surface="create_outreach_modal"
                            ehrDisplayName={ehrDisplayName}
                            className="flex-shrink-0"
                        />
                    )}
                    {!isEditMode && shiftsMissingCarePlanData.length > 0 && (
                        <MissingCarePlanBanner
                            chrome={chrome}
                            missingShifts={shiftsMissingCarePlanData}
                            organizationId={organizationId ?? null}
                            onSaved={async (plans) => {
                                // Merge generated plans into perShiftCarePlans
                                // so outreach creation embeds them in metadata.
                                // Without this the first SMS goes out with an
                                // empty care plan section even though the
                                // coordinator just saved a summary.
                                setPerShiftCarePlans((prev) => ({
                                    ...prev,
                                    ...plans,
                                }));
                                // Optimistically dismiss only the clients whose
                                // shifts actually received plans. Shifts with
                                // no draft are excluded from plans, so their
                                // banners must stay visible.
                                const savedShiftIds = new Set(
                                    Object.keys(plans),
                                );
                                const clientIds = new Set(
                                    shiftsMissingCarePlanData
                                        .filter((s) => savedShiftIds.has(s.id))
                                        .map((s) => s.client_id)
                                        .filter((id): id is string => !!id),
                                );
                                setSavedCarePlanClientIds(
                                    (prev) => new Set([...prev, ...clientIds]),
                                );
                                await mutateShifts();
                            }}
                            className="flex-shrink-0"
                        />
                    )}
                    <div
                        ref={scrollContainerRef}
                        data-testid="create-outreach-modal-scroll-container"
                        className="min-h-0 min-w-0 flex-1 touch-pan-y space-y-6 overflow-x-hidden overflow-y-auto overscroll-contain pr-2"
                    >
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="template-select">Type</Label>
                                <div className="flex min-w-0 items-center gap-2">
                                    <Select
                                        value={selectedTemplateType}
                                        onValueChange={(value) =>
                                            setSelectedTemplateType(
                                                value as OutreachTemplate['outreach_type'],
                                            )
                                        }
                                    >
                                        <SelectTrigger
                                            id="template-select"
                                            className="min-w-0 flex-1"
                                            disabled={
                                                isLoadingTemplates ||
                                                isManualEntry
                                            }
                                        >
                                            <SelectValue
                                                placeholder={
                                                    isLoadingTemplates
                                                        ? 'Loading templates...'
                                                        : 'Select a template'
                                                }
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {templateOptions.map((option) => (
                                                <SelectItem
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {selectedTemplateType === 'callout' ? (
                                    <p className="text-muted-foreground text-sm">
                                        Open shift outreach is used to fill a
                                        one-time shift temporarily — for
                                        example, when a caregiver calls out sick
                                        or is on vacation.
                                    </p>
                                ) : selectedTemplateType ===
                                  'recurring_shift' ? (
                                    <p className="text-muted-foreground text-sm">
                                        Recurring shift outreach is used to fill
                                        a shift on an ongoing basis with the
                                        same caregiver.
                                    </p>
                                ) : null}
                                {outreachTemplates.length === 0 &&
                                !isLoadingTemplates ? (
                                    <p className="text-muted-foreground text-sm">
                                        No open shift templates are available
                                        yet.
                                    </p>
                                ) : null}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="shift-select">Shifts</Label>
                                <div className="flex min-w-0 items-start gap-2">
                                    <div className="min-w-0 flex-1">
                                        <MultiShiftCombobox
                                            disabled={isManualEntry}
                                            values={selectedShiftIds}
                                            onValuesChange={setSelectedShiftIds}
                                            shifts={filteredShifts}
                                            isLoading={isLoadingShifts}
                                            emptyStateMessage="No uncovered shifts in the next 14 days. Search by client or caregiver name to find later shifts."
                                            scrollLockRef={scrollContainerRef}
                                            onLoadMore={loadMoreShifts}
                                            hasMore={hasMoreShifts}
                                            isLoadingMore={
                                                isLoadingShifts && offset > 0
                                            }
                                            onShiftDiscovered={
                                                handleShiftDiscovered
                                            }
                                            currentOutreachId={
                                                outreachToEdit?.id
                                            }
                                            allowDiscoveryReplacement={
                                                allowDiscoveryReplacement
                                            }
                                        />
                                    </div>
                                </div>
                                {(isLoadingShifts && allShifts.length === 0) ||
                                !hasShifts ||
                                !shouldUseQuickOutreachForm ? (
                                    <p className="text-muted-foreground text-sm">
                                        {isLoadingShifts &&
                                        allShifts.length === 0
                                            ? 'Loading uncovered shifts…'
                                            : hasShifts
                                              ? 'Select the shifts caregivers will be contacted about.'
                                              : 'No uncovered shifts in the next 14 days. Search by client or caregiver name to find later shifts.'}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                        {showOutreachModeTabs ? (
                            <div className="space-y-2">
                                <Tabs
                                    value={outreachComposerMode}
                                    onValueChange={(value) => {
                                        setOutreachComposerMode(
                                            value === 'agent'
                                                ? 'agent'
                                                : 'manual',
                                        );
                                    }}
                                    className="w-full"
                                >
                                    <TabsList className="grid w-full grid-cols-2">
                                        <TabsTrigger value="agent">
                                            Automatic
                                        </TabsTrigger>
                                        <TabsTrigger value="manual">
                                            Manual
                                        </TabsTrigger>
                                    </TabsList>
                                </Tabs>
                                {outreachComposerMode === 'manual' ? (
                                    <p className="text-muted-foreground text-sm">
                                        Use the original outreach composer to
                                        configure and start manually.
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                        <ShiftEditorSection
                            organization={organization}
                            selectedShifts={groupedSelectedShifts}
                            shiftGroupsValue={shiftGroupsValue}
                            onShiftGroupsChange={setShiftGroupsValue}
                            collapseGroupingControls={
                                shouldUseQuickOutreachForm
                            }
                            groupingTooltip={
                                shouldUseQuickOutreachForm
                                    ? 'Phoebe will group according to the outreach playbook guidance if it differs from the default grouping configuration. You can also tell Phoebe how you want the shifts grouped in the chat.'
                                    : undefined
                            }
                            showGroupingControls={showGroupingControls}
                            onShowGroupingControlsChange={
                                setShowGroupingControls
                            }
                            showAutoAssign={supportsAutoAssignFirstResponder}
                            autoAssignShiftIds={autoAssignShiftIds}
                            autoAssignPolicy={
                                organization?.callout_settings
                                    ?.auto_assign_caregiver_policy
                            }
                            onAutoAssignShiftIdsChange={
                                handleAutoAssignShiftIdsChange
                            }
                        />
                        {shouldRenderAutoAssignModifyReason ? (
                            <div className="rounded-md border p-3">
                                <HHAExchangeModifyReasonSelector
                                    reasons={
                                        autoAssignModifyReasonsData?.reasons
                                    }
                                    selectedReasonId={autoAssignModifyReasonId}
                                    defaultReasonId={
                                        autoAssignModifyReasonsData?.default_reason_id
                                    }
                                    isLoading={isLoadingAutoAssignModifyReasons}
                                    error={autoAssignModifyReasonsError}
                                    onReasonChange={setAutoAssignModifyReasonId}
                                />
                            </div>
                        ) : null}
                        {supportsAutoAssignFirstResponder &&
                        !isEditMode &&
                        autoAssignFirstResponderEnabled ? (
                            <div className="rounded-md border p-3">
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="auto-assign-notify-non-responders"
                                        checked={autoAssignNotifyNonResponders}
                                        onCheckedChange={(checked) =>
                                            setAutoAssignNotifyNonResponders(
                                                checked === true,
                                            )
                                        }
                                        className="mt-1"
                                    />
                                    <Label
                                        htmlFor="auto-assign-notify-non-responders"
                                        className="cursor-pointer text-sm font-medium"
                                    >
                                        Notify non-responding caregivers if
                                        auto-assign fills a shift.
                                    </Label>
                                </div>
                            </div>
                        ) : null}
                        {shouldUseQuickOutreachForm ? (
                            <QuickOutreachForm
                                driveMode="automatic"
                                selectedShiftIds={nonMissingSelectedShiftIds}
                                selectedShifts={nonMissingSelectedShifts}
                                shiftGroups={activeShiftGroups}
                                timezone={organization?.timezone}
                                onClose={handleClose}
                                onSuccess={onSuccess}
                                navigateOnSuccess={
                                    navigateToOutreachOnAgentSuccess
                                }
                                carePlanSummary={carePlanSummary}
                                carePlanBullets={carePlanBullets}
                                perShiftCarePlans={perShiftCarePlans}
                                isLoadingCarePlan={isLoadingCarePlan}
                                outreachType={selectedTemplateType}
                                autoAssignShiftIds={autoAssignShiftIdsForSubmit}
                                autoAssignNotifyNonResponders={
                                    autoAssignNotifyNonResponders
                                }
                                autoAssignModifyReasonId={
                                    shouldRenderAutoAssignModifyReason
                                        ? autoAssignModifyReasonId
                                        : null
                                }
                                selectedTemplate={selectedTemplate}
                                analyticsContext={analyticsContext}
                                exceedsShiftLimit={
                                    selectedShiftIds.length > selectedShiftLimit
                                }
                                selectedShiftCount={selectedShiftIds.length}
                                shiftLimit={selectedShiftLimit}
                                creationBlockedReason={creationBlockedReason}
                                onCancelledShiftSelection={(shiftIds) => {
                                    const rejected =
                                        shiftIds.length > 0
                                            ? shiftIds
                                            : selectedShiftIds;
                                    // Block on the server's answer first, so
                                    // a failed refetch cannot leave submit
                                    // enabled and loop the coordinator.
                                    setRejectedCancelledShiftIds(
                                        (previous) =>
                                            new Set([...previous, ...rejected]),
                                    );
                                    void refreshShiftsById(rejected);
                                }}
                            />
                        ) : (
                            <CreateOutreachForm
                                creationAction={manualEntry}
                                onClose={handleClose}
                                onSuccess={onSuccess}
                                navigateOnSuccess={
                                    isEditMode
                                        ? false
                                        : navigateToOutreachOnManualSuccess
                                }
                                selectedTemplate={selectedTemplate}
                                selectedShiftIds={nonMissingSelectedShiftIds}
                                selectedShifts={nonMissingSelectedShifts}
                                shiftGroups={activeShiftGroups}
                                carePlanSummary={carePlanSummary}
                                carePlanBullets={carePlanBullets}
                                isLoadingCarePlan={isLoadingCarePlan}
                                perShiftCarePlans={perShiftCarePlans}
                                onCarePlanSummaryChange={
                                    handleCarePlanSummaryChange
                                }
                                onCarePlanBulletsChange={
                                    handleCarePlanBulletsChange
                                }
                                onPerShiftCarePlanChange={
                                    handlePerShiftCarePlanChange
                                }
                                autoAssignShiftIds={autoAssignShiftIdsForSubmit}
                                autoAssignNotifyNonResponders={
                                    autoAssignNotifyNonResponders
                                }
                                autoAssignModifyReasonId={
                                    shouldRenderAutoAssignModifyReason
                                        ? autoAssignModifyReasonId
                                        : null
                                }
                                supportsAutoAssignFirstResponder={
                                    supportsAutoAssignFirstResponder
                                }
                                editContext={editContext}
                                analyticsContext={analyticsContext}
                                exceedsShiftLimit={
                                    selectedShiftIds.length > selectedShiftLimit
                                }
                                selectedShiftCount={selectedShiftIds.length}
                                shiftLimit={selectedShiftLimit}
                                creationBlockedReason={creationBlockedReason}
                                onCancelledShiftSelection={(shiftIds) => {
                                    const rejected =
                                        shiftIds.length > 0
                                            ? shiftIds
                                            : selectedShiftIds;
                                    setRejectedCancelledShiftIds(
                                        (previous) =>
                                            new Set([...previous, ...rejected]),
                                    );
                                    void refreshShiftsById(rejected);
                                }}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}
